import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Archive, Megaphone, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";

const EMPTY = { title: "", body: "", tag: "NOTICE", date: "", important: false, status: "published" };
const TAGS = ["NOTICE", "IMPORTANT", "UPDATE", "EVENT"];

export default function AdminNotices() {
  const [notices, setNotices] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/notices")
      .then((r) => setNotices(r.data))
      .catch(() => setNotices([]));

  useEffect(() => {
    load();
  }, []);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const startEdit = (n) => {
    setEditingId(n.id);
    setForm({ title: n.title, body: n.body, tag: n.tag, date: n.date, important: n.important, status: n.status });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/admin/notices/${editingId}`, form);
        toast.success("Notice updated.");
      } else {
        await api.post("/admin/notices", form);
        toast.success("Notice published.");
      }
      reset();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const quickStatus = async (n, status) => {
    try {
      await api.patch(`/admin/notices/${n.id}`, { ...n, status });
      toast.success(status === "retired" ? "Notice retired." : "Notice published.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (n) => {
    if (!window.confirm(`Delete notice "${n.title}" permanently?`)) return;
    try {
      await api.delete(`/admin/notices/${n.id}`);
      toast.success("Notice deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AdminLayout title="Notices Management" testId="admin-notices">
      <form
        onSubmit={submit}
        data-testid="notice-form"
        className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">
            {editingId ? `Edit notice ${editingId}` : "Publish a new notice"}
          </h2>
          {editingId && (
            <button
              type="button"
              onClick={reset}
              data-testid="notice-cancel-edit-button"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Cancel
            </button>
          )}
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label htmlFor="notice-title" className="field-label">Title</label>
            <input
              id="notice-title"
              data-testid="notice-title-input"
              className="field-input"
              value={form.title}
              onChange={set("title")}
              required
              minLength={2}
              placeholder="e.g. Free digital literacy camp this Saturday"
            />
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="notice-body" className="field-label">Content</label>
            <textarea
              id="notice-body"
              data-testid="notice-body-input"
              className="field-input min-h-28 resize-y"
              value={form.body}
              onChange={set("body")}
              required
              minLength={5}
              placeholder="Full notice text…"
            />
          </div>
          <div>
            <label htmlFor="notice-tag" className="field-label">Tag</label>
            <select
              id="notice-tag"
              data-testid="notice-tag-select"
              className="field-input"
              value={form.tag}
              onChange={set("tag")}
            >
              {TAGS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="notice-date" className="field-label">Date</label>
            <input
              id="notice-date"
              type="date"
              data-testid="notice-date-input"
              className="field-input"
              value={form.date}
              onChange={set("date")}
            />
          </div>
          <label className="flex items-center gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="notice-important-checkbox"
              checked={form.important}
              onChange={set("important")}
              className="h-4 w-4 rounded border-slate-300 accent-blue-900"
            />
            Highlight as important
          </label>
          <div>
            <label htmlFor="notice-status" className="field-label">Status</label>
            <select
              id="notice-status"
              data-testid="notice-status-select"
              className="field-input"
              value={form.status}
              onChange={set("status")}
            >
              <option value="published">Published</option>
              <option value="retired">Retired (hidden)</option>
            </select>
          </div>
        </div>
        <button
          type="submit"
          data-testid="notice-submit-button"
          disabled={saving}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-blue-900 px-6 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800 disabled:opacity-50"
        >
          <Megaphone className="h-4 w-4" aria-hidden="true" />
          {saving ? "Saving…" : editingId ? "Save changes" : "Publish notice"}
        </button>
      </form>

      <div className="mt-10 space-y-4">
        {notices === null && (
          <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" data-testid="admin-notices-loading" />
        )}
        {notices !== null && notices.length === 0 && (
          <p className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-600" data-testid="admin-notices-empty">
            No notices yet. Publish the first one above.
          </p>
        )}
        {notices !== null &&
          notices.map((n) => (
            <article
              key={n.id}
              data-testid={`admin-notice-${n.id}`}
              className={`rounded-xl border bg-white p-6 shadow-sm ${
                n.status === "retired" ? "border-slate-200 opacity-60" : n.important ? "border-l-4 border-l-amber-500 border-slate-200" : "border-slate-200"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-blue-50 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-blue-900">
                  {n.tag}
                </span>
                {n.important && (
                  <span className="rounded-full bg-amber-100 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                    Important
                  </span>
                )}
                {n.status === "retired" && (
                  <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    Retired
                  </span>
                )}
                <span className="font-mono text-[11px] text-slate-500">{n.date}</span>
              </div>
              <h3 className="mt-2 font-display text-lg font-semibold tracking-tight text-slate-900">{n.title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{n.body}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => startEdit(n)}
                  data-testid={`notice-edit-${n.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-blue-900 hover:text-blue-900"
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  Edit
                </button>
                {n.status === "published" ? (
                  <button
                    onClick={() => quickStatus(n, "retired")}
                    data-testid={`notice-retire-${n.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-amber-400 hover:text-amber-700"
                  >
                    <Archive className="h-3.5 w-3.5" aria-hidden="true" />
                    Retire
                  </button>
                ) : (
                  <button
                    onClick={() => quickStatus(n, "published")}
                    data-testid={`notice-publish-${n.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-green-400 hover:text-green-700"
                  >
                    <Megaphone className="h-3.5 w-3.5" aria-hidden="true" />
                    Publish
                  </button>
                )}
                <button
                  onClick={() => remove(n)}
                  data-testid={`notice-delete-${n.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-red-300 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  Delete
                </button>
              </div>
            </article>
          ))}
      </div>
    </AdminLayout>
  );
}
